import React from 'react';
import { Box, Typography } from '@mui/material';
import {
    UsersIcon,
    ActivityIcon,
    FolderIcon,
    SettingsIcon,
    GlobeIcon,
} from '../../../Icons/DashboardIcons';

/**
 * Tab configuration with icon mapping
 */
const TAB_ICONS = {
    'User Statistics': UsersIcon,
    Engagement: ActivityIcon,
    Projects: FolderIcon,
    'Machine Usage': SettingsIcon,
    'MIS Data': GlobeIcon,
};

/**
 * Default tabs for dashboard
 */
export const DEFAULT_DASHBOARD_TABS = [
    'User Statistics',
    'Engagement',
    'Projects',
    'Machine Usage',
    'MIS Data',
];

/**
 * Reusable Dashboard Tabs Component
 * Used by both TLDashboard and MBFDashboard
 *
 * @param {array} tabs - Array of tab names to display
 * @param {string} activeTab - Currently active tab name
 * @param {function} onTabChange - Callback when tab is changed
 */
const DashboardTabs = ({
    tabs = DEFAULT_DASHBOARD_TABS,
    activeTab = 'User Statistics',
    onTabChange,
}) => {
    const handleTabClick = (tabName) => {
        if (onTabChange) {
            onTabChange(tabName);
        }
    };

    return (
        <Box
            sx={{
                bgcolor: '#ffffff',
                borderRadius: { xs: '8px', md: '12px' },
                padding: { xs: '6px', md: '8px' },
                mb: { xs: 2, md: 3 },
                boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.05)',
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    gap: { xs: '8px', sm: '12px', md: '20px' },
                    alignItems: 'center',
                    minWidth: 'max-content',
                }}
            >
                {tabs.map((tabName) => {
                    const IconComponent = TAB_ICONS[tabName];
                    const isActive = activeTab === tabName;

                    return (
                        <Box
                            key={tabName}
                            onClick={() => handleTabClick(tabName)}
                            sx={{
                                borderBottom: isActive ? '2px solid #007bff' : 'none',
                                padding: '8px',
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'center',
                                cursor: 'pointer',
                                '&:hover': {
                                    opacity: 0.8,
                                },
                            }}
                        >
                            {IconComponent && (
                                <IconComponent color={isActive ? '#007bff' : '#4d4d4d'} />
                            )}
                            <Typography
                                sx={{
                                    fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                    fontWeight: isActive ? 500 : 400,
                                    fontFamily: isActive
                                        ? '"Roboto-Medium", "Roboto", sans-serif'
                                        : '"Roboto-Regular", "Roboto", sans-serif',
                                    color: isActive ? '#007bff' : '#4d4d4d',
                                    lineHeight: '20px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {tabName}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

export default DashboardTabs;
